import type { HandednessStateAPI } from "@/state/handedness";
import type { Handedness } from "@/state/handedness";
import { zHandedness } from "@/data/schemas";

interface HandednessAdapterDeps {
  readonly store: any;
  readonly eventBus: any;
  readonly getPath: (key: string) => string | null;
  readonly persist: () => void;
  readonly handedness?: HandednessStateAPI | null;
}

const isHandedness = (value: unknown): value is Handedness =>
  value === "left" || value === "right";

export const hydrateHandednessFromSession = (
  deps: Pick<HandednessAdapterDeps, "handedness">,
  screen: Record<string, unknown> | null | undefined
) => {
  if (!deps.handedness || !screen) return;
  const raw =
    (screen as Record<string, unknown>).handedness ??
    (screen as Record<string, unknown>).dominantHand;
  if (!isHandedness(raw)) return;
  deps.handedness.set(raw, { source: "hydration" });
};

export const registerHandednessAdapter = (
  deps: HandednessAdapterDeps
): (() => void) => {
  const { store, eventBus, getPath, persist, handedness } = deps;
  if (!handedness) return () => {};

  const unsubscribes: (() => void)[] = [];

  const readDebugEnabled = (): boolean => {
    try {
      const raw =
        (globalThis as any)?.localStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.sessionStorage?.getItem?.("debug.adapters") ||
        (globalThis as any)?.__debugAdapters ||
        "";
      if (typeof raw !== "string") {
        if (raw && typeof raw === "object")
          return !!(raw as Record<string, unknown>).handedness;
        return raw === "*";
      }
      const parts = raw.split(",").map((s: string) => s.trim());
      return parts.includes("*") || parts.includes("handedness");
    } catch {
      return false;
    }
  };

  let debugEnabled = readDebugEnabled();
  const debug = (...args: unknown[]) => {
    if (!debugEnabled) return;
    try {
      // eslint-disable-next-line no-console
      console.debug("[adapter:handedness]", ...args);
    } catch {
      // Ignore logging failures
    }
  };

  try {
    const unsubDbg = eventBus.on(
      "debug:adapter",
      (payload: { name?: string; enabled?: boolean } | undefined) => {
        if (!payload) return;
        try {
          if (payload.name === "handedness")
            debugEnabled = !!payload.enabled;
          if (payload.name === "*" && typeof payload.enabled === "boolean")
            debugEnabled = !!payload.enabled;
        } catch {
          // Ignore toggle failures
        }
      }
    );
    unsubscribes.push(unsubDbg);
  } catch {
    // Ignore debug subscription failures
  }

  const shouldPersist = (source?: string) =>
    source !== "session" &&
    source !== "socket" &&
    source !== "store" &&
    source !== "hydration" &&
    source !== "init";

  const ensureSchemas = (paths: readonly (string | null)[]) => {
    for (const path of paths) {
      if (!path) continue;
      try {
        store.registerSchema(path, zHandedness);
      } catch {
        // Ignore duplicate registration errors
      }
    }
  };

  const writeToStore = (value: Handedness, metaSource?: string) => {
    const handednessPath = getPath("handedness");
    const dominantHandPath = getPath("dominantHand");
    if (!handednessPath && !dominantHandPath) return;

    ensureSchemas([handednessPath, dominantHandPath]);

    if (handednessPath) {
      store.set(handednessPath, value, { source: "handednessAdapter" });
      debug("state→store handedness", { value, metaSource });
    }
    if (dominantHandPath) {
      store.set(dominantHandPath, value, { source: "handednessAdapter" });
      debug("state→store dominantHand", { value, metaSource });
    }

    if (shouldPersist(metaSource)) {
      persist();
    }
  };

  const unsubscribeState = handedness.subscribe(
    (value, meta: { source?: string }) => {
      if (!isHandedness(value)) return;
      if (meta.source === "store" || meta.source === "socket") return;
      writeToStore(value, meta.source);
    }
  );
  unsubscribes.push(unsubscribeState);

  const unsubscribeStore = store.subscribe(
    (path: string) =>
      path.endsWith(".screenState.handedness") ||
      path.endsWith(".screenState.dominantHand"),
    ({
      value,
      source,
    }: {
      value: unknown;
      source: string | undefined;
    }) => {
      if (source === "handednessAdapter") return;
      if (!isHandedness(value)) return;
      debug("store→state handedness", { value, source });
      handedness.set(value, { source: source ?? "store" });
      if (source && source !== "socket" && source !== "hydration") {
        persist();
      }
    },
    { debounceMs: 50 }
  );
  unsubscribes.push(unsubscribeStore);

  try {
    const unsubProjectChanged = eventBus.on(
      "project-changed",
      () => writeToStore(handedness.get(), "project-change")
    );
    unsubscribes.push(unsubProjectChanged);
  } catch {
    // Ignore project change subscription failures
  }

  try {
    const unsubAppDestroy = eventBus.on("app:destroy", () => {
      for (const unsub of unsubscribes) {
        try {
          unsub();
        } catch {
          // Ignore teardown failures
        }
      }
    });
    unsubscribes.push(unsubAppDestroy);
  } catch {
    // Ignore destroy subscription failures
  }

  return () => {
    for (const unsub of unsubscribes) {
      try {
        unsub();
      } catch {
        // Ignore teardown failures
      }
    }
  };
};
